import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeOff, Pin } from 'lucide-react';
import Redacted from '../Redacted';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_VISIBLE = 6;

// Distinct hue + glow per category - keeps the glass board visually varied
const CATEGORY_TONE = {
  'Gangland':     { glow: 'rgba(239, 68, 68, 0.35)',  pin: '#ef4444', tint: 'from-red-500/15  to-red-900/5'  },
  'Tobacco War':  { glow: 'rgba(249, 115, 22, 0.35)', pin: '#fb923c', tint: 'from-orange-500/15 to-orange-900/5' },
  'Drug Network': { glow: 'rgba(168, 85, 247, 0.35)', pin: '#a855f7', tint: 'from-purple-500/15 to-purple-900/5' },
  'Corruption':   { glow: 'rgba(234, 179, 8, 0.30)',  pin: '#eab308', tint: 'from-yellow-500/15 to-yellow-900/5' },
  'Crime Scene':  { glow: 'rgba(244, 63, 94, 0.35)',  pin: '#f43f5e', tint: 'from-rose-500/15 to-rose-900/5' },
  'Other':        { glow: 'rgba(148, 163, 184, 0.25)', pin: '#94a3b8', tint: 'from-slate-500/10 to-slate-900/5' },
  'General':      { glow: 'rgba(148, 163, 184, 0.25)', pin: '#94a3b8', tint: 'from-slate-500/10 to-slate-900/5' },
};

const formatShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short',
    timeZone: 'Australia/Melbourne',
  });
};

// A single floating glass tip card with a pin + subtle rotation
const TipShard = ({ tip, index }) => {
  const tone = CATEGORY_TONE[tip.category] || CATEGORY_TONE['Other'];
  // Pseudo-random rotation seeded by index for a "pinned-by-hand" look
  const rotations = [-2.4, 1.6, -1.1, 2.3, -1.8, 1.2, -2.7, 0.9];
  const rot = rotations[index % rotations.length];
  const delay = `${(index * 90) + 100}ms`;

  return (
    <article
      data-testid={`tipwall-shard-${tip.id}`}
      className="tipwall-shard group relative break-inside-avoid mb-3 sm:mb-4 opacity-0"
      style={{
        animation: `shard-in 0.55s ease-out forwards`,
        animationDelay: delay,
        transform: `rotate(${rot}deg)`,
      }}
    >
      {/* Pin */}
      <span
        aria-hidden="true"
        className="absolute -top-1.5 left-1/2 -translate-x-1/2 z-10 h-3 w-3 rounded-full ring-2 ring-black/40"
        style={{
          background: `radial-gradient(circle at 30% 30%, #fff, ${tone.pin} 60%, ${tone.pin})`,
          boxShadow: `0 0 8px ${tone.pin}, 0 2px 4px rgba(0,0,0,0.5)`,
        }}
      />

      {/* Glass card */}
      <div
        className={`relative rounded-lg p-3 sm:p-4 pt-4 sm:pt-5 backdrop-blur-md bg-gradient-to-br ${tone.tint} border border-white/10 group-hover:border-white/25 transition-all duration-300 group-hover:-translate-y-0.5`}
        style={{ boxShadow: `0 8px 20px -8px ${tone.glow}, inset 0 1px 0 rgba(255,255,255,0.08)` }}
      >
        {/* Scratched-glass highlight */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-lg opacity-40"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 35%, transparent 70%, rgba(255,255,255,0.04) 100%)',
          }}
        />

        <div className="relative flex items-center justify-between mb-1.5">
          <span
            className="inline-flex items-center gap-1 text-[9px] font-black tracking-[0.2em] uppercase"
            style={{ color: tone.pin }}
          >
            <EyeOff className="h-2.5 w-2.5" />
            {tip.category || 'Tip'}
          </span>
          <span className="text-[9px] text-white/40 tracking-widest uppercase">
            {formatShortDate(tip.published_at)}
          </span>
        </div>

        <p className="relative text-[12px] sm:text-[13px] text-gray-100/95 leading-snug whitespace-pre-wrap break-words font-mono tracking-tight">
          <Redacted text={tip.redacted_text} />
        </p>
      </div>
    </article>
  );
};

// Placeholder "ghost" tip - shown when there are no published tips yet
const GhostShard = ({ index }) => {
  const rotations = [-2.4, 1.6, -1.1, 2.3, -1.8, 1.2];
  const rot = rotations[index % rotations.length];
  const delay = `${(index * 90) + 100}ms`;
  const widths = ['w-3/4', 'w-full', 'w-2/3', 'w-5/6', 'w-1/2'];
  return (
    <article
      className="break-inside-avoid mb-3 sm:mb-4 opacity-0"
      style={{
        animation: `shard-in 0.55s ease-out forwards`,
        animationDelay: delay,
        transform: `rotate(${rot}deg)`,
      }}
      aria-hidden="true"
    >
      <span
        className="absolute -top-1.5 left-1/2 -translate-x-1/2 z-10 h-3 w-3 rounded-full ring-2 ring-black/40 bg-red-500/70"
        style={{ boxShadow: '0 0 6px rgba(239,68,68,0.6)' }}
      />
      <div
        className="relative rounded-lg p-3 sm:p-4 pt-4 sm:pt-5 backdrop-blur-md bg-gradient-to-br from-slate-500/10 to-slate-900/5 border border-white/5"
        style={{ boxShadow: '0 8px 20px -8px rgba(148,163,184,0.18), inset 0 1px 0 rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="h-1.5 w-12 rounded bg-white/10" />
          <div className="h-1.5 w-8 rounded bg-white/10" />
        </div>
        <div className="space-y-1.5">
          {widths.slice(0, 3 + (index % 2)).map((w, i) => (
            <div key={i} className={`h-1.5 rounded bg-white/10 ${w}`} />
          ))}
        </div>
      </div>
    </article>
  );
};

const TipWallShowcase = () => {
  const [tips, setTips] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/tips/wall`, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        if (!cancelled) {
          setTips(Array.isArray(data) ? data.slice(0, MAX_VISIBLE) : []);
          setLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const showGhosts = loaded && tips.length === 0;

  return (
    <section
      className="relative px-4 py-6 sm:py-8"
      data-testid="tipwall-showcase"
      style={{ minHeight: 320 }}
    >
      {/* Ambient red haze behind the glass */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-10 h-72 opacity-50"
        style={{
          background:
            'radial-gradient(60% 60% at 50% 30%, rgba(220,38,38,0.18) 0%, rgba(220,38,38,0.04) 45%, transparent 80%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Header strip - editorial-style, no icon + kicker + shouty caps */}
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <h2 className="text-lg md:text-xl font-black tracking-tight leading-tight">
            The <span className="text-red-500">Rumour File</span>
          </h2>
          <Link
            to="/the-rumour-file"
            data-testid="tipwall-showcase-see-all"
            className="text-[11px] font-bold uppercase tracking-wider text-red-400 hover:text-red-300 transition-colors"
          >
            View full file →
          </Link>
        </div>

        {/* Glass board container */}
        <div
          className="relative rounded-2xl border border-white/10 overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 50%, rgba(0,0,0,0.2) 100%)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(255,255,255,0.04), 0 30px 60px -20px rgba(220,38,38,0.18)',
          }}
        >
          {/* Faint grid lines for the "digital board" feel */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
              maskImage:
                'radial-gradient(ellipse at center, black 40%, transparent 90%)',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, black 40%, transparent 90%)',
            }}
          />

          {/* Top + bottom edge gleams */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.5), transparent)' }}
          />

          {/* Corner pins (decorative) */}
          {[
            'top-2 left-2', 'top-2 right-2',
            'bottom-2 left-2', 'bottom-2 right-2',
          ].map((pos) => (
            <span
              key={pos}
              aria-hidden="true"
              className={`absolute ${pos} h-1.5 w-1.5 rounded-full bg-red-500/70 ring-2 ring-black/40`}
              style={{ boxShadow: '0 0 8px rgba(239,68,68,0.7)' }}
            />
          ))}

          {/* Masonry grid of tips */}
          <div className="relative p-4 sm:p-6 columns-2 md:columns-3 gap-3 sm:gap-4">
            {!loaded && Array.from({ length: 3 }).map((_, i) => (
              <GhostShard key={`loading-${i}`} index={i} />
            ))}

            {loaded && tips.length > 0 && tips.map((t, i) => (
              <TipShard key={t.id} tip={t} index={i} />
            ))}

            {showGhosts && (
              <>
                <div className="break-inside-avoid mb-3">
                  <div
                    className="relative rounded-lg p-4 backdrop-blur-md border border-red-500/30 bg-red-950/20 text-center"
                    style={{ boxShadow: '0 8px 20px -8px rgba(220,38,38,0.35)' }}
                  >
                    <Pin className="h-4 w-4 text-red-400 mx-auto mb-2" />
                    <p className="text-[11px] font-black uppercase tracking-widest text-red-300">
                      The board is empty
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1 leading-snug">
                      Be the first to drop intel - published tips land here.
                    </p>
                    <Link
                      to="/tip"
                      data-testid="tipwall-showcase-empty-cta"
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest"
                    >
                      Submit a Tip
                    </Link>
                  </div>
                </div>
                {Array.from({ length: 5 }).map((_, i) => (
                  <GhostShard key={`ghost-${i}`} index={i + 1} />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-3 text-[10px] text-gray-500 tracking-widest uppercase text-center">
          Every tip redacted by hand. No IPs. No fingerprints. No exceptions.
        </p>
      </div>

      <style>{`
        @keyframes shard-in {
          from { opacity: 0; transform: translateY(12px) rotate(var(--r, 0deg)); }
          to   { opacity: 1; transform: translateY(0)    rotate(var(--r, 0deg)); }
        }
      `}</style>
    </section>
  );
};

export default TipWallShowcase;
